<template>
    <v-container fluid>
        <v-row class="fill-height justify-center align-center">
            <v-col cols="6" cols-xs="12">
                <v-card v-show="!(disabledUserMessage || showForgotPassword)">
                    <v-card-title>
                        Welcome back!
                    </v-card-title>
                    <v-card-text class="pt-3">
                        <v-form ref="form" v-model="isFormValid">
                            <v-text-field
                                v-model="userName"
                                label="Username *"
                                :rules="userNameRules"
                                required
                                :error-messages="usernameErrors"
                                @keyup.enter="onFieldKeyEnter"
                            ></v-text-field>
                            <v-text-field
                                v-model="password"
                                label="Password *"
                                :rules="passwordRules"
                                required
                                type="password"
                                :error-messages="passwordErrors"
                                @keyup.enter="onFieldKeyEnter"
                            ></v-text-field>
                        </v-form>
                    </v-card-text>
                    <v-card-actions class="pb-4 px-4 flex-column">
                        <v-btn
                            block
                            text
                            color="primary"
                            class="mb-3 text-none"
                            @click="onForgotPasswordClick"
                            >Forgot your password?</v-btn
                        >
                        <v-btn
                            block
                            color="primary"
                            @click="onPartnerUserLoginClick"
                            >Sign In</v-btn
                        >
                    </v-card-actions>
                </v-card>
                <v-card v-show="showForgotPassword">
                    <v-card-title>
                        <v-btn
                            icon
                            small
                            color="accent"
                            class="mr-4"
                            aria-label="Go back to login screen"
                            @click="onBackToLoginClick"
                        >
                            <v-icon large>{{ icons.arrowBack }}</v-icon>
                        </v-btn>
                        <span>Reset your password</span>
                    </v-card-title>
                    <v-card-text class="pt-3">
                        <div class="text-body-1">
                            Enter your email below and we'll send you a link to
                            reset your password
                        </div>
                        <v-form ref="form2" v-model="isForm2Valid">
                            <v-text-field
                                v-model="userName"
                                label="Email *"
                                required
                                :rules="userNameRules"
                                :error-messages="usernameErrors"
                                @keydown.enter.prevent="onFieldKeyEnter"
                            ></v-text-field>
                        </v-form>
                    </v-card-text>
                    <v-card-actions class="pb-4 px-4">
                        <v-btn block class="primary" @click="onSendEmailClick"
                            >Send reset password email</v-btn
                        >
                    </v-card-actions>
                </v-card>
                <div
                    v-if="disabledUserMessage"
                    class="warning--text text-h1 pt-5 text-xs-center"
                >
                    {{ disabledUserMessage }}
                </div>
            </v-col>
        </v-row>
        <v-row
            v-if="!disabledUserMessage"
            row
            justify-end
            class="justify-end mt-n8"
        >
            <v-btn text color="primary" @click="onEmployeeLoginClick">
                <span class="mr-2 text-none"
                    >Service Operators Log In Here</span
                >
                <v-icon>{{ icons.login }}</v-icon>
            </v-btn>
        </v-row>
    </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
    name: 'LoginPage',
    data() {
        return {
            isFormValid: true,
            isForm2Valid: true,
            userName: '',
            userNameRules: [
                v => !!v || 'Username is required',
                v =>
                    (v && v.length <= 200) ||
                    'Username must be less than 10 characters'
            ],
            usernameErrors: [],
            password: '',
            passwordRules: [
                v => !!v || 'Password is required',
                v =>
                    (v && v.length <= 128) ||
                    'Password must be less than 10 characters'
            ],
            passwordErrors: [],
            disabledUserMessage: '',
            showForgotPassword: false
        };
    },
    computed: {
        ...mapState('brand', ['mso']),
        msoLoginUrl() {
            return this.$store.state.msoLoginUrl;
        }
    },
    methods: {
        ...mapActions('authUser', ['forgotPassword']),
        // ====================== Events ==============================
        onEmployeeLoginClick() {
            // will get the current user SSO token from xerxes api
            // convert that token to app token
            // get the user info
            // and pass that to the application login page
            const redirectPath = this.$router.currentRoute.query.redirect;
            const continueUrl = encodeURIComponent(
                `${window.location.origin}/`
            );
            if (redirectPath) {
                // need to store this in localstorage because Xerxes does not
                // support redirect query params
                localStorage.setItem('redirect', redirectPath);
            }
            const xerxesApiParams = {
                url: this.msoLoginUrl,
                clientId: 'vms',
                responseType: 'code',
                continue: continueUrl
            };
            const newLocation =
                `${xerxesApiParams.url}?client_id=${xerxesApiParams.clientId}` +
                `&response_type=${xerxesApiParams.responseType}` +
                `&continue=${xerxesApiParams.continue}`;
            console.log('farward url to Xerxes:', newLocation);
            window.location = newLocation;
        },
        async onPartnerUserLoginClick() {
            // will perform user login using the username and password
            if (this.$refs.form.validate()) {
                const data = {
                    username: this.userName,
                    password: this.password
                };
                try {
                    await this.$store.dispatch('authUser/login', data);
                    this.$refs.form.reset();
                    const redirectPath = this.getRedirectPath();
                    if (redirectPath) {
                        this.$router.push(redirectPath);
                    } else {
                        this.$router.push({ name: 'homePage' });
                    }
                } catch (error) {
                    // need to display error messages back to the form
                    if (error.response && error.response.status === 403) {
                        this.passwordErrors.push(
                            'Incorrect Username or Password'
                        );
                    } else if (
                        error.response &&
                        error.response.status === 409
                    ) {
                        this.$router.push({ name: 'changePasswordPage' });
                    } else if (
                        error.response &&
                        error.response.status === 423
                    ) {
                        this.setDisabledUserMessage(true);
                    } else {
                        console.log('login error:', error);
                        this.passwordErrors.push(
                            'Incorrect Username or Password'
                        );
                    }
                }
            }
        },
        onForgotPasswordClick() {
            this.$refs.form2.resetValidation();
            this.$refs.form.resetValidation();
            this.showForgotPassword = true;
        },
        onBackToLoginClick() {
            this.password = '';
            this.$refs.form.resetValidation();
            this.$refs.form2.resetValidation();
            this.showForgotPassword = false;
        },
        async onSendEmailClick() {
            if (this.$refs.form2.validate()) {
                try {
                    await this.forgotPassword({
                        email: this.userName,
                        url: `${window.location.origin}/change-password`,
                        mso: this.mso
                    });
                    // go back to the login form
                    this.password = '';
                    this.usernameErrors = '';
                    this.$refs.form2.reset();
                    this.showForgotPassword = false;
                } catch (error) {
                    if (
                        error.response.status === 400 ||
                        error.response.status === 404
                    ) {
                        this.usernameErrors = error.response.data.message;
                    } else {
                        this.usernameErrors = 'API call error!';
                    }
                }
            }
        },
        onFieldKeyEnter() {
            if (this.showForgotPassword) {
                this.onSendEmailClick();
            } else {
                this.onPartnerUserLoginClick();
            }
        },
        // ===================== Others ==========================
        setDisabledUserMessage(isNonComcastuser) {
            if (isNonComcastuser) {
                this.disabledUserMessage =
                    'Sorry, your account has been disabled, please contact your site administrator for more information.';
            } else {
                this.disabledUserMessage =
                    'Please contact ACE Console Team to get permissions to access this site. Email:\n' +
                    'Compass_ACEConsole@comcast.com';
            }
        },
        getRedirectPath() {
            let redirectPath = this.$router.currentRoute.query.redirect || '';
            return redirectPath;
        }
    }
};
</script>
